import { supabase } from '@/integrations/supabase/client';
import type { AppDatabase } from '@/integrations/supabase/app-types';
import type { HelpArticle, HelpCategory, HelpSearchResult, HelpSearchFilters } from '@/types/help';
import { logger } from '@/lib/logger';

const toHelpCategory = (category: {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  icon_name: string | null;
  sort_order: number | null;
  is_active: boolean | null;
  created_at: string | null;
  updated_at: string | null;
}): HelpCategory => ({
  id: category.id,
  name: category.name,
  slug: category.slug,
  description: category.description || undefined,
  icon_name: category.icon_name || undefined,
  sort_order: category.sort_order ?? 0,
  is_active: category.is_active ?? true,
  created_at: category.created_at || new Date(0).toISOString(),
  updated_at: category.updated_at || category.created_at || new Date(0).toISOString(),
});

const toHelpArticle = (article: {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string | null;
  category: string;
  tags: string[] | null;
  sort_order: number | null;
  is_published: boolean | null;
  created_at: string | null;
  updated_at: string | null;
}): HelpArticle => ({
  id: article.id,
  title: article.title,
  slug: article.slug,
  content: article.content,
  excerpt: article.excerpt || undefined,
  category: article.category,
  tags: article.tags || [],
  sort_order: article.sort_order ?? 0,
  is_published: article.is_published ?? true,
  created_at: article.created_at || new Date(0).toISOString(),
  updated_at: article.updated_at || article.created_at || new Date(0).toISOString(),
});

export class HelpService {
  /**
   * Get all help categories
   */
  static async getCategories(): Promise<HelpCategory[]> {
    const { data, error } = await supabase
      .from('help_categories')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });

    if (error) {
      logger.error('Error fetching help categories:', error);
      throw error;
    }

    return (data || []).map(toHelpCategory);
  }

  /**
   * Get all articles for a specific category
   */
  static async getArticlesByCategory(categorySlug: string): Promise<HelpArticle[]> {
    const { data, error } = await supabase
      .from('help_articles')
      .select('*')
      .eq('category', categorySlug)
      .eq('is_published', true)
      .order('sort_order', { ascending: true });

    if (error) {
      logger.error('Error fetching articles by category:', error);
      throw error;
    }

    return (data || []).map(toHelpArticle);
  }

  /**
   * Get a specific article by slug
   */
  static async getArticleBySlug(slug: string): Promise<HelpArticle | null> {
    const { data, error } = await supabase
      .from('help_articles')
      .select('*')
      .eq('slug', slug)
      .eq('is_published', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned
        return null;
      }
      logger.error('Error fetching article by slug:', error);
      throw error;
    }

    return toHelpArticle(data);
  }

  /**
   * Search help articles using full-text search
   */
  static async searchArticles(
    query: string,
    filters: HelpSearchFilters = {}
  ): Promise<HelpSearchResult[]> {
    if (!query.trim()) {
      return [];
    }

    let queryBuilder = supabase
      .from('help_articles')
      .select('*')
      .textSearch('search_vector', query, {
        type: 'plain',
        config: 'english'
      })
      .eq('is_published', true);

    // Apply filters
    if (filters.category) {
      queryBuilder = queryBuilder.eq('category', filters.category);
    }

    if (filters.tags && filters.tags.length > 0) {
      queryBuilder = queryBuilder.overlaps('tags', filters.tags);
    }

    // Order by sort_order as fallback (articles are pre-ordered by relevance in content)
    queryBuilder = queryBuilder.order('sort_order', { ascending: true });

    const { data, error } = await queryBuilder;

    if (error) {
      logger.error('Error searching articles:', error);
      throw error;
    }

    return (data || []).map((item, index) => ({
      article: toHelpArticle(item),
      rank: 1.0 - (index * 0.01), // Simple decreasing rank for UI purposes
    }));
  }

  /**
   * Get recent/popular articles
   */
  static async getFeaturedArticles(limit: number = 5): Promise<HelpArticle[]> {
    const { data, error } = await supabase
      .from('help_articles')
      .select('*')
      .eq('is_published', true)
      .order('sort_order', { ascending: true })
      .limit(limit);

    if (error) {
      logger.error('Error fetching featured articles:', error);
      throw error;
    }

    return (data || []).map(toHelpArticle);
  }

  /**
   * Record article view for analytics
   */
  static async recordArticleView(articleId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('help_article_views')
        .insert({
          article_id: articleId,
          user_agent: navigator.userAgent,
        } as AppDatabase['public']['Tables']['help_article_views']['Insert'] as never);

      if (error) {
        logger.warn('Error recording article view:', error);
        // Don't throw error for analytics failures
      }
    } catch (err) {
      logger.warn('Error recording article view:', err);
      // Don't throw error for analytics failures
    }
  }

  /**
   * Get articles with similar tags (for "Related Articles")
   */
  static async getRelatedArticles(
    currentArticle: HelpArticle, 
    limit: number = 3
  ): Promise<HelpArticle[]> {
    if (!currentArticle.tags || currentArticle.tags.length === 0) {
      // Fallback to same category
      return this.getArticlesByCategory(currentArticle.category);
    }

    const { data, error } = await supabase
      .from('help_articles')
      .select('*')
      .overlaps('tags', currentArticle.tags)
      .neq('id', currentArticle.id)
      .eq('is_published', true)
      .order('sort_order', { ascending: true })
      .limit(limit);

    if (error) {
      logger.error('Error fetching related articles:', error);
      // Fallback to same category
      return this.getArticlesByCategory(currentArticle.category);
    }

    return (data || []).map(toHelpArticle);
  }
}
